import { Flag, LayoutDashboard } from "lucide-mui";
import Skeleton from "@mui/material/Skeleton";
import Switch from "@mui/material/Switch";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import toast from "react-hot-toast";
import { api, type FeatureFlagDefinition, type Organization } from "../../../api/client.ts";
import { keys } from "../../../api/queryKeys.ts";
import { FEATURE_MICRO_APPS } from "../../../auth/features.ts";
import AccordionCard from "../../../components/AccordionCard.tsx";
import SettingRow from "../../../components/SettingRow.tsx";
import { StatusLabel } from "../../../components/StatusIcon.tsx";
import { useAuth } from "../../../contexts/AuthContext.tsx";

const ICONS: Record<string, ReactNode> = {
  [FEATURE_MICRO_APPS]: <LayoutDashboard />,
};

/**
 * The organization's feature flags, one switch each. Staff only: the page is
 * global-admin/developer only, and so are the endpoints behind it.
 */
export default function OrganizationFeaturesPanel({ org }: { org: Organization }) {
  const queryClient = useQueryClient();
  const { refreshUser } = useAuth();
  const detailKey = keys.organization.detail(org.id);

  const { data: flags, isLoading } = useQuery({
    queryKey: keys.organization.featureFlags(),
    queryFn: api.getFeatureFlags,
    staleTime: Infinity,
  });

  const toggle = useMutation({
    mutationFn: ({ flag, enabled }: { flag: FeatureFlagDefinition; enabled: boolean }) =>
      api.setOrganizationFeature(org.id, flag.key, enabled),
    // Flip at once; the server's answer (or the snapshot, on failure) settles it.
    onMutate: async ({ flag, enabled }) => {
      await queryClient.cancelQueries({ queryKey: detailKey });
      const previous = queryClient.getQueryData<Organization>(detailKey);
      if (previous) {
        const rest = previous.features.filter((f) => f !== flag.key);
        queryClient.setQueryData<Organization>(detailKey, {
          ...previous,
          features: enabled ? [...rest, flag.key] : rest,
        });
      }
      return { previous };
    },
    onError: (_err, { flag }, context) => {
      if (context?.previous) queryClient.setQueryData(detailKey, context.previous);
      toast.error(`Could not change ${flag.label.toLowerCase()}`);
    },
    onSuccess: (updated, { flag, enabled }) => {
      queryClient.setQueryData(detailKey, updated);
      queryClient.invalidateQueries({ queryKey: keys.organization.list() });
      // Menus read features from the signed-in user's org list.
      void refreshUser();
      toast.success(`${flag.label} turned ${enabled ? "on" : "off"} for ${org.name}`);
    },
    meta: { suppressErrorToast: true },
  });

  const enabled = flags?.filter((f) => org.features.includes(f.key)) ?? [];
  const summary = !flags
    ? "What this organization can use"
    : enabled.length === 0
      ? "Every feature off"
      : `${enabled.map((f) => f.label).join(", ")} on`;

  return (
    <AccordionCard
      logo={<Flag sx={{ color: "text.secondary" }} />}
      title="Features"
      count={flags?.length}
      summary={`${summary} · only developers and global admins see or change these`}
      disablePadding
    >
      {isLoading || !flags ? (
        <SettingRow
          icon={<Flag />}
          label={<Skeleton width={120} />}
          description={<Skeleton width={280} />}
        />
      ) : (
        flags.map((flag) => {
          const on = org.features.includes(flag.key);
          const pending = toggle.isPending && toggle.variables?.flag.key === flag.key;
          return (
            <SettingRow
              key={flag.key}
              icon={ICONS[flag.key] ?? <Flag />}
              label={flag.label}
              description={
                flag.default_for_new_orgs
                  ? flag.description
                  : `${flag.description} New organizations start without it.`
              }
              action={
                <>
                  <StatusLabel kind={on ? "ok" : "off"} label={on ? "On" : "Off"} />
                  <Switch
                    checked={on}
                    disabled={pending}
                    onChange={(e) => toggle.mutate({ flag, enabled: e.target.checked })}
                    slotProps={{ input: { "aria-label": `${flag.label} for ${org.name}` } }}
                  />
                </>
              }
            />
          );
        })
      )}
    </AccordionCard>
  );
}
